import React, { useState, useMemo } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, TableAction, SupportRule } from '@shared/table/TableIndex'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface ProductCategoriesListProps {
  role?: 'operator' | 'admin';
  service: TableService; // New prop
}

interface ProductCategoryRow {
  id: string;
  name?: string;
  created_at?: string;
  [key: string]: unknown;
}

const ProductCategoriesList: React.FC<ProductCategoriesListProps> = ({
  role = 'operator',
  service, // New prop
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const columns = useMemo<unknown[]>(() => {
    return [
      {
        nameKey: `models.product_category.name`,
        persist: true,
        sortable: true,
        sortFieldName: 'product_categories.name',
        width: '300px',
        wrap: true,
        cell: ({ id, name }: ProductCategoryRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/product_categories/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`${urlPrefix}/product_categories/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'product_categories.id',
        wrap: true,
        selector: ({ created_at }: ProductCategoryRow) => created_at,
      }
    ]
  }, [urlPrefix])

  const supportRules = useMemo<SupportRule[]>(() => {
    return [
      {
        label: t(`shared.product_categories.list.filters.name`),
        component: MultipleValue,
        inputName: 'product_categories.name',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.product_categories.list.filter_tooltips.name.title'),
          content: t('shared.product_categories.list.filter_tooltips.name.content'),
        },
      }
    ]
  }, [t])

  const actions = useMemo<TableAction[]>(() => [], [])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'prefix',
        searchFields: [t('models.product_category.name')],
      }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ProductCategoriesTable`}
      defaultQuery={{
        sorting_field: 'product_categories.id',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      actions={actions}
      service={service}
      filterHelpExample={t('shared.product_categories.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/product_categories/list#features`}
    />
  )
}

export default ProductCategoriesList
